import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { useLocation, useHistory } from "react-router-dom";
import { Button } from 'primereact/button';
import Paginator from '../../../components/Paginator';
import { NotAvailable } from '../../../components/Loading';
import SurveyTempListHeader from './SurveyTempListHeader';
import { toSortOrder, parse, sortUrl } from '../../../utils/UrlUtil';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { AccountContext } from '../../../components/AccountContext';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  success: { id: 'noti.success', defaultMessage: 'Success!' },
  title: { id: 'attrs.title', defaultMessage: 'Title' },
  abilities: { id: 'attrs.ability.plural', defaultMessage: 'Abilities' },
  updated: { id: 'attrs.updated', defaultMessage: 'Updated' },
  action: { id: 'attrs.actions', defaultMessage: 'Actions' },
  delete: { id: 'actions.delete', defaultMessage: 'Delete' },
  created: { id: 'attrs.created', defaultMessage: 'Created' },
});

const nameTemplate = (rowData, { currentAccount, keyField, displayField }) => {
  const detailUrl = `/accounts/${currentAccount}/surveys/templates/${rowData[keyField]}`;
  return (
    <Link to={detailUrl} className="app-link">
      {rowData[displayField]}
    </Link>
  );
};

const timeFieldTemplate = (rowData, { formatDate, field }) => {
  const updated = formatDate(moment(rowData[field]), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

const SurveyTemplateList = ({
  connection: { edges, totalCount, indexFrom, indexTo, pageInfo },
  loading,
  deleteTemplate,
}) => {
  const location = useLocation();
  const history = useHistory();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage, formatDate } = useIntl();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const onSortChange = (event) => {
    sortUrl(event, location, history);
  };

  const actionsTemplate = (rowData) => {
    const { canDelete } = rowData;

    return (
      <Button
        icon="pi pi-times"
        className="p-button-secondary"
        disabled={!canDelete || subscriptionIsExpired}
        onClick={() => handleDeleteTemplate(rowData.id)}
      />
    );
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await deleteTemplate({
        variables: {
          input: {
            accountId: currentAccount,
            surveyTemplateId: id,
          },
        },
      });
      growl.show({ severity: 'info', detail: formatMessage(messages.success) });
    } catch (err) {
      growl.show({ severity: 'error', sticky: true, detail: err.message });
    }
  };

  // data list
  const listRows = edges.map(({ node }) => ({
    ...node,
    abilities: node.abilities.length,
    action: <NotAvailable />,
  }));

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  // header
  const nameHeader = formatMessage(messages.title);
  const abilitiesHeader = formatMessage(messages.abilities);
  const updatedHeader = formatMessage(messages.updated);
  const deleteHeader = formatMessage(messages.delete);
  const createdHeader = formatMessage(messages.created);

  // footer
  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-col-12">
                <SurveyTempListHeader currentAccount={currentAccount} />
              </div>
              <div className="p-col-12 align-center">
                <DataTable
                  autoLayout
                  value={listRows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={onSortChange}
                  lazy
                  loading={loading}
                >
                  <Column
                    field="title"
                    header={nameHeader}
                    body={nameTemplate}
                    keyField="id"
                    displayField="title"
                    currentAccount={currentAccount}
                    sortable
                    style={{ textAlign: 'left' }}
                  />
                  <Column field="abilities" header={abilitiesHeader} />
                  <Column
                    field="createdOn"
                    header={createdHeader}
                    body={timeFieldTemplate}
                    formatDate={formatDate}
                    sortable
                  />
                  <Column
                    field="updatedOn"
                    header={updatedHeader}
                    body={timeFieldTemplate}
                    formatDate={formatDate}
                    sortable
                  />
                  <Column
                    header={deleteHeader}
                    body={actionsTemplate}
                    deleteTemplate={deleteTemplate}
                    currentAccount={currentAccount}
                    growl={growl}
                    formatMessage={formatMessage}
                    style={{ width: '3.5em' }}
                  />
                </DataTable>
              </div>
              <div className="p-col-12 align-center">{footer}</div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

SurveyTemplateList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
  deleteTemplate: PropTypes.func.isRequired,
};

export default SurveyTemplateList;
